import { z } from 'zod';
import {
  type AssertTrue,
  type ACTOR_ATTRIBUTES,
  type ASSET_ATTRIBUTES,
  type CanonicalModel,
  type CanonicalObjectIndex,
  type COMPONENT_ATTRIBUTES,
  type CONTROL_FLOW_ATTRIBUTES,
  controlsCovering,
  type DATA_FLOW_ATTRIBUTES,
  type ENVIRONMENT_ATTRIBUTES,
  type ExactKeys,
  type EXISTING_CONTROL_ATTRIBUTES,
  type SAFETY_FUNCTION_ATTRIBUTES,
  type SYSTEM_ATTRIBUTES,
  SYSTEM_OBJECT_ID,
  type TRUST_BOUNDARY_ATTRIBUTES,
} from './canonical';
import { attributeConfidence } from './confidence';
import {
  type CanonicalObjectType,
  CanonicalObjectTypeSchema,
  COMPONENT_TYPES,
  CONTROL_KINDS,
  type ControlKind,
  type Confidence,
  CONTROL_PLANES,
  DATA_CLASSIFICATIONS,
  ENVIRONMENT_KINDS,
  PRIMARY_GROUNDING_TYPES,
  TRUST_BOUNDARY_KINDS,
  TRUST_LEVELS,
} from './enums';
import { establishedAttributes, isAbsenceMarker, type Provenanced } from './provenance';

/**
 * The applicability predicate (docs/DECISIONS.md ZD-029).
 *
 * `applicability.predicate` was a `NonEmptyString` from sds-00 to sds-04 and
 * nothing parsed it, which left two things stranded: `predicate_basis`, which
 * `validateFinding` inferred conservatively, and `depends_on_attributes`, which
 * ZD-020 filled with *every* separately-established attribute of every grounded
 * object because nothing could say which ones a pattern reads.
 *
 * Three properties make this module what those two were waiting for:
 *
 *  1. **Typed, not parsed.** A Zod-validated tree is either well-formed or
 *     refused at the pack's own parse. A string parser would fail at load time
 *     on content a model authored, which is the worst moment to discover it.
 *  2. **Three-valued.** An attribute the model never established evaluates to
 *     `unknown`, never to its literal value — so a `[]` nothing populated can
 *     never be read as a denial (ZD-027 rule 2, generalised).
 *  3. **Witnessing.** Evaluation returns the attributes it actually consulted
 *     and the objects it actually reached, so `depends_on_attributes` and
 *     `grounded_object_ids` are *derived* rather than declared beside the
 *     predicate where nothing could check them.
 */

// ---------------------------------------------------------------------------
// The vocabulary a predicate may name
// ---------------------------------------------------------------------------

/**
 * The system is readable but not groundable (ZD-029 §7): it is the subject of
 * the assessment rather than an object in it, so it has no minted id and is
 * absent from `indexCanonicalObjects` — but `system.multi_tenant` is a core AI
 * predicate and has to be reachable.
 */
export type PredicateObjectType = CanonicalObjectType | 'system';

/**
 * How a predicate reaches a related object. A closed list, because every
 * member is a structural relation the canonical model already holds — and
 * because an open one would be a query language, which is where a
 * deterministic rule stops being writable (CLAUDE.md principle 2).
 */
export const PREDICATE_NAVIGATIONS = [
  'source',
  'destination',
  'endpoint',
  'crossed_boundary',
  'carried_asset',
  'applied_control',
  'separated_object',
  'resident_component',
  'system',
] as const;
export const PredicateNavigationSchema = z.enum(PREDICATE_NAVIGATIONS);
export type PredicateNavigation = z.infer<typeof PredicateNavigationSchema>;

/** What a predicate may do with one attribute — and, by omission, what it may not. */
export type AttributeShape =
  /** A closed vocabulary. `equals` and `one_of` are permitted, against these values. */
  | { kind: 'scalar'; values: readonly (string | boolean)[] }
  /**
   * A list. `empty` is permitted; membership is expressed by navigating to it.
   *
   * `references` names the object types the list holds ids of, empty for a list
   * of plain strings. It is what lets `reachableObjectTypes` see that a pattern
   * testing `crosses_boundary_ids` depends on a trust boundary existing without
   * ever navigating to one — otherwise `requires_object_types` could not name
   * the type the pattern genuinely needs.
   */
  | { kind: 'collection'; references: readonly CanonicalObjectType[] }
  /**
   * A list drawn from a **closed vocabulary** — the only collection whose
   * members a predicate may compare, through `includes`.
   *
   * Distinct from `collection` because the two answer different questions. A
   * `collection` holds ids or free strings, so membership is expressed by
   * navigating to what it references; there is nothing to compare a literal
   * against. This one holds enum values, so `includes` can ask *"does this
   * control do any of these things"* — which is the whole of ZD-039 — while the
   * free-text rule stays intact, because `technologies` is not one of these.
   */
  | { kind: 'enum_collection'; values: readonly string[] }
  /**
   * Free text. **Nothing is permitted**, deliberately: matching prose is where a
   * deterministic rule stops being writable, and ZD-026 §3 already established
   * that a deterministic component cannot tell "more specific" from "different".
   */
  | { kind: 'text' }
  /** A single id. Reachable by navigation, not comparable by value. */
  | { kind: 'reference' };

const TEXT: AttributeShape = { kind: 'text' };
const REFERENCE: AttributeShape = { kind: 'reference' };
const collection = (...references: CanonicalObjectType[]): AttributeShape => ({
  kind: 'collection',
  references,
});
const scalar = (values: readonly (string | boolean)[]): AttributeShape => ({
  kind: 'scalar',
  values,
});
const enumCollection = (values: readonly string[]): AttributeShape => ({
  kind: 'enum_collection',
  values,
});

/**
 * Whether an attribute holds a list — the shape that can be *unestablished* and
 * therefore undecidable (ZD-029 §3).
 *
 * **Exported because two readers need the same answer.** `validatePredicate`
 * uses it to decide where `empty` applies, and the corpus's blocked-list
 * completeness check uses it to decide which attributes are in scope. That
 * check took its scope off `PREDICATE_ATTRIBUTES` deliberately (ZD-036 §7) so
 * it could not drift from the rule it implements — and then compared against
 * the literal `'collection'`, so the moment ZD-039 added a second list shape the
 * new attribute left scope silently. Reading the shape through one function is
 * what makes "read off the schema" true rather than nearly true.
 */
export const isListShape = (shape: AttributeShape): boolean =>
  shape.kind === 'collection' || shape.kind === 'enum_collection';

/** `Tristate` is `boolean | 'unknown'`, so its vocabulary is not all strings. */
export const TRISTATE_VALUES = [true, false, 'unknown'] as const;

/**
 * Every attribute a predicate may name, by object type.
 *
 * The keys are pinned to the same `*_ATTRIBUTES` vocabularies
 * `attribute_provenance` is keyed on (see the assertions below), because those
 * two readers must never disagree: a predicate naming an attribute the
 * provenance vocabulary does not hold would produce a witness
 * `attributeConfidence` cannot resolve, and `validateFinding` would reject the
 * finding for a reason that is really an authoring error two layers away.
 */
export const PREDICATE_ATTRIBUTES = {
  component: {
    name: TEXT,
    type: scalar(COMPONENT_TYPES),
    description: TEXT,
    environment_id: REFERENCE,
    internet_facing: scalar(TRISTATE_VALUES),
    technologies: collection(),
  },
  actor: { name: TEXT, trust: scalar(TRUST_LEVELS), description: TEXT },
  asset: {
    name: TEXT,
    data_classification: scalar(DATA_CLASSIFICATIONS),
    /*
     * Free text, matching `description`'s shape and not `data_classification`'s
     * — a pack pattern may still write `eq('data_subject', 'not_applicable')`,
     * but no pattern does yet (docs/DECISIONS.md ZD-076 §2). Kept in the
     * vocabulary for the key-set equality this file's own comment requires,
     * not as an invitation.
     */
    data_subject: TEXT,
    description: TEXT,
    resides_in: collection('component'),
  },
  data_flow: {
    from: REFERENCE,
    to: REFERENCE,
    protocol: TEXT,
    data: TEXT,
    asset_ids: collection('asset'),
    encrypted: scalar(TRISTATE_VALUES),
    authenticated: scalar(TRISTATE_VALUES),
    crosses_boundary_ids: collection('trust_boundary'),
  },
  control_flow: {
    from: REFERENCE,
    to: REFERENCE,
    plane: scalar(CONTROL_PLANES),
    protocol: TEXT,
    privileged: scalar(TRISTATE_VALUES),
    authenticated: scalar(TRISTATE_VALUES),
    crosses_boundary_ids: collection('trust_boundary'),
  },
  trust_boundary: {
    name: TEXT,
    kind: scalar(TRUST_BOUNDARY_KINDS),
    separates: collection('component', 'actor', 'environment'),
  },
  existing_control: {
    name: TEXT,
    description: TEXT,
    kinds: enumCollection(CONTROL_KINDS),
    applies_to: collection('component', 'data_flow', 'control_flow', 'trust_boundary'),
  },
  environment: {
    name: TEXT,
    kind: scalar(ENVIRONMENT_KINDS),
    cloud: TEXT,
    region: TEXT,
    data_residency: TEXT,
  },
  safety_function: { name: TEXT, description: TEXT, protects: collection() },
  system: {
    name: TEXT,
    description: TEXT,
    internet_facing: scalar(TRISTATE_VALUES),
    multi_tenant: scalar(TRISTATE_VALUES),
  },
} satisfies Record<PredicateObjectType, Record<string, AttributeShape>>;

/**
 * Key-set equality against the provenance vocabularies, in both directions.
 *
 * The same posture as `AssertedProvenancedObjects` in `./canonical`, and for
 * the same reason: a one-way `extends` would accept a table that is missing an
 * attribute, and a missing attribute here is a predicate an author cannot write
 * with no error saying why. What this still cannot see — dropping a member from
 * a vocabulary *constant*, which moves both sides at once — is covered by the
 * runtime test in `predicate.test.ts`.
 */
export type AssertedPredicateAttributes = [
  AssertTrue<
    ExactKeys<keyof typeof PREDICATE_ATTRIBUTES.component, (typeof COMPONENT_ATTRIBUTES)[number]>
  >,
  AssertTrue<ExactKeys<keyof typeof PREDICATE_ATTRIBUTES.actor, (typeof ACTOR_ATTRIBUTES)[number]>>,
  AssertTrue<ExactKeys<keyof typeof PREDICATE_ATTRIBUTES.asset, (typeof ASSET_ATTRIBUTES)[number]>>,
  AssertTrue<
    ExactKeys<keyof typeof PREDICATE_ATTRIBUTES.data_flow, (typeof DATA_FLOW_ATTRIBUTES)[number]>
  >,
  AssertTrue<
    ExactKeys<
      keyof typeof PREDICATE_ATTRIBUTES.control_flow,
      (typeof CONTROL_FLOW_ATTRIBUTES)[number]
    >
  >,
  AssertTrue<
    ExactKeys<
      keyof typeof PREDICATE_ATTRIBUTES.trust_boundary,
      (typeof TRUST_BOUNDARY_ATTRIBUTES)[number]
    >
  >,
  AssertTrue<
    ExactKeys<
      keyof typeof PREDICATE_ATTRIBUTES.existing_control,
      (typeof EXISTING_CONTROL_ATTRIBUTES)[number]
    >
  >,
  AssertTrue<
    ExactKeys<
      keyof typeof PREDICATE_ATTRIBUTES.environment,
      (typeof ENVIRONMENT_ATTRIBUTES)[number]
    >
  >,
  AssertTrue<
    ExactKeys<
      keyof typeof PREDICATE_ATTRIBUTES.safety_function,
      (typeof SAFETY_FUNCTION_ATTRIBUTES)[number]
    >
  >,
  AssertTrue<
    ExactKeys<keyof typeof PREDICATE_ATTRIBUTES.system, (typeof SYSTEM_ATTRIBUTES)[number]>
  >,
];

interface NavigationSpec {
  from: readonly PredicateObjectType[];
  to: readonly PredicateObjectType[];
  /** The attribute on the *source* object this navigation reads, if any. */
  reads: readonly string[];
  /** Prose for `describePredicate`. */
  phrase: string;
}

export const NAVIGATION_SPECS: Record<PredicateNavigation, NavigationSpec> = {
  source: {
    from: ['data_flow', 'control_flow'],
    to: ['component', 'actor'],
    reads: ['from'],
    phrase: 'its source',
  },
  destination: {
    from: ['data_flow', 'control_flow'],
    to: ['component', 'actor'],
    reads: ['to'],
    phrase: 'its destination',
  },
  endpoint: {
    from: ['data_flow', 'control_flow'],
    to: ['component', 'actor'],
    reads: ['from', 'to'],
    phrase: 'its endpoints',
  },
  crossed_boundary: {
    from: ['data_flow', 'control_flow'],
    to: ['trust_boundary'],
    reads: ['crosses_boundary_ids'],
    phrase: 'the trust boundaries it crosses',
  },
  carried_asset: {
    from: ['data_flow'],
    to: ['asset'],
    reads: ['asset_ids'],
    phrase: 'the assets it carries',
  },
  applied_control: {
    from: ['component', 'data_flow', 'control_flow'],
    to: ['existing_control'],
    // Read through `controlsCovering`, which reports what it consulted (ZD-030).
    reads: [],
    phrase: 'the observed controls that apply to it',
  },
  separated_object: {
    from: ['trust_boundary'],
    to: ['component', 'actor', 'environment'],
    reads: ['separates'],
    phrase: 'what it separates',
  },
  resident_component: {
    from: ['asset'],
    to: ['component'],
    reads: ['resides_in'],
    phrase: 'the components it resides in',
  },
  system: {
    from: [...CanonicalObjectTypeSchema.options, 'system'],
    to: ['system'],
    reads: [],
    phrase: 'the system',
  },
};

// ---------------------------------------------------------------------------
// The expression
// ---------------------------------------------------------------------------

export const PredicateQuantifierSchema = z.enum(['some', 'every']);
export type PredicateQuantifier = z.infer<typeof PredicateQuantifierSchema>;

export const AttributeValueSchema = z.union([z.string(), z.boolean()]);
export type AttributeValue = z.infer<typeof AttributeValueSchema>;

export type PredicateExpr =
  | { kind: 'all'; of: PredicateExpr[] }
  | { kind: 'any'; of: PredicateExpr[] }
  | { kind: 'not'; of: PredicateExpr }
  | {
      kind: 'where';
      path: PredicateNavigation;
      /**
       * Narrows a navigation whose targets are a union — `endpoint` reaches a
       * component *or* an actor, and `trust` exists on only one of them. Null
       * where the navigation already resolves to one type. Required-and-nullable
       * rather than optional, matching the package's convention that parsing
       * round-trips exactly.
       */
      of_type: CanonicalObjectType | null;
      quantifier: PredicateQuantifier;
      of: PredicateExpr;
    }
  | { kind: 'equals'; attribute: string; value: AttributeValue }
  | { kind: 'one_of'; attribute: string; values: AttributeValue[] }
  /**
   * A closed-vocabulary collection holds at least one of these values
   * (docs/DECISIONS.md ZD-039).
   *
   * Three-valued on establishment, exactly as `empty` is: a collection nothing
   * populated yields `unknown`, never `false`. That is what makes *"no observed
   * control of kind X applies here"* refuse to be answered from a control whose
   * purpose the documents never stated.
   */
  | { kind: 'includes'; attribute: string; values: AttributeValue[] }
  /**
   * A scalar attribute **was established**, and its value is one of these
   * (docs/DECISIONS.md ZD-040).
   *
   * The three-valued counterpart of `one_of`, and the only node that can tell a
   * document's **stated negative** from its silence. `equals`/`one_of` compare
   * the literal, so `authenticated == false` is decidably false against the
   * absence-marker and `authenticated == 'unknown'` is decidably true — both
   * legitimate (ZD-029 section 3) and neither able to say *"the document says
   * this path is not authenticated"* as distinct from *"nobody said"*. Nor can
   * any composition say it: `where`, `empty` and `includes` can yield `unknown`
   * and none reads a scalar, every scalar node is two-valued, and Kleene
   * composition of two-valued nodes never produces `unknown`.
   *
   * So: established and matching is `true`, established and not matching is
   * `false`, and unestablished is `unknown` — which routes to the pattern's own
   * `unknown_policy` (ZD-029 section 4) instead of asserting in either
   * direction. Keyed off `establishedAttributes`, exactly as `empty` and
   * `includes` are, so all three list-and-scalar forms share one rule.
   *
   * `values` may not name an absence-marker: such a clause can never be `true`,
   * which is an authoring error that would show up only as a pattern that never
   * fires. `equals(attribute, 'unknown')` remains the way to ask whether the
   * documents are silent.
   */
  | { kind: 'established_as'; attribute: string; values: AttributeValue[] }
  | { kind: 'empty'; attribute: string }
  | { kind: 'present' };

export const PredicateExprSchema: z.ZodType<PredicateExpr> = z.lazy(() =>
  z.union([
    z.strictObject({ kind: z.literal('all'), of: z.array(PredicateExprSchema).min(1) }),
    z.strictObject({ kind: z.literal('any'), of: z.array(PredicateExprSchema).min(1) }),
    z.strictObject({ kind: z.literal('not'), of: PredicateExprSchema }),
    z.strictObject({
      kind: z.literal('where'),
      path: PredicateNavigationSchema,
      of_type: CanonicalObjectTypeSchema.nullable(),
      quantifier: PredicateQuantifierSchema,
      of: PredicateExprSchema,
    }),
    z.strictObject({
      kind: z.literal('equals'),
      attribute: z.string().min(1),
      value: AttributeValueSchema,
    }),
    z.strictObject({
      kind: z.literal('one_of'),
      attribute: z.string().min(1),
      values: z.array(AttributeValueSchema).min(1),
    }),
    z.strictObject({
      kind: z.literal('includes'),
      attribute: z.string().min(1),
      values: z.array(AttributeValueSchema).min(1),
    }),
    z.strictObject({
      kind: z.literal('established_as'),
      attribute: z.string().min(1),
      values: z.array(AttributeValueSchema).min(1),
    }),
    z.strictObject({ kind: z.literal('empty'), attribute: z.string().min(1) }),
    z.strictObject({ kind: z.literal('present') }),
  ]),
);

// ---------------------------------------------------------------------------
// Static validation, against the subject type
// ---------------------------------------------------------------------------

export interface PredicateIssue {
  message: string;
  path: (string | number)[];
}

/**
 * The declared shape of one attribute, or `undefined` where the type has none.
 *
 * **Exported for the same reason `isListShape` is**: a second reader needs the
 * same answer, and the way this codebase gets that wrong is by computing it
 * again. `buildClarification` asks what an answer to a question must look
 * like — a closed vocabulary, a list of ids, free text — and the only honest
 * source for that is the vocabulary the predicate itself was checked against
 * (ZD-029 §2). A parallel table of "what we ask the client for" would be a
 * second copy of `PREDICATE_ATTRIBUTES` that nothing compares against the first.
 */
export const shapeOf = (type: PredicateObjectType, attribute: string): AttributeShape | undefined =>
  (PREDICATE_ATTRIBUTES[type] as Record<string, AttributeShape | undefined>)[attribute];

/**
 * Checks an expression against the object type it will be evaluated on.
 *
 * Refusals are authoring errors, and each one is a place the predicate would
 * otherwise be silently unsatisfiable: an attribute the type does not have, a
 * value outside a closed vocabulary, a comparison against free text, a
 * navigation the type cannot make. All are decidable here and none is decidable
 * at evaluation time, where the only symptom would be a pattern that never
 * fires and nothing saying why.
 */
export function validatePredicate(
  expr: PredicateExpr,
  subjectType: PredicateObjectType,
): PredicateIssue[] {
  const issues: PredicateIssue[] = [];

  const walk = (
    node: PredicateExpr,
    types: readonly PredicateObjectType[],
    path: (string | number)[],
  ): void => {
    switch (node.kind) {
      case 'present':
        return;

      case 'all':
      case 'any':
        node.of.forEach((child, i) => {
          walk(child, types, [...path, 'of', i]);
        });
        return;

      case 'not':
        walk(node.of, types, [...path, 'of']);
        return;

      case 'equals':
      case 'one_of':
      case 'includes':
      case 'established_as':
      case 'empty': {
        // An `established_as` naming an absence-marker can never be `true`:
        // an attribute holding one is never in `establishedAttributes`, so the
        // node answers `unknown` before it compares anything. Refused here
        // rather than left to be discovered as a pattern that never fires —
        // the same posture as ZD-039's refusal of `other` in a kind filter, and
        // for the same reason. Asking whether the documents are silent is
        // `equals(attribute, 'unknown')`, which stays decidable (ZD-029 §3).
        if (node.kind === 'established_as') {
          for (const value of node.values) {
            if (!isAbsenceMarker(value)) continue;
            issues.push({
              message: `"established_as" may not name the absence-marker ${String(value)}: an attribute holding it is never established, so the clause can never hold`,
              path: [...path, 'values'],
            });
          }
        }
        const applies = (shape: AttributeShape): boolean =>
          node.kind === 'empty'
            ? isListShape(shape)
            : node.kind === 'includes'
              ? shape.kind === 'enum_collection'
              : shape.kind === 'scalar';
        for (const type of types) {
          const shape = shapeOf(type, node.attribute);
          if (!shape) {
            issues.push({
              message: `${type} has no attribute "${node.attribute}"`,
              path: [...path, 'attribute'],
            });
            continue;
          }
          if (!applies(shape)) {
            issues.push({
              message:
                shape.kind === 'text'
                  ? `${type}.${node.attribute} is free text; a deterministic predicate may not match prose`
                  : `${type}.${node.attribute} is a ${shape.kind}, so "${node.kind}" does not apply to it`,
              path: [...path, 'attribute'],
            });
            continue;
          }
          if (shape.kind !== 'scalar' && shape.kind !== 'enum_collection') continue;
          const values: AttributeValue[] =
            node.kind === 'equals'
              ? [node.value]
              : node.kind === 'one_of' || node.kind === 'includes' || node.kind === 'established_as'
                ? node.values
                : [];
          for (const value of values) {
            if (!(shape.values as readonly AttributeValue[]).includes(value)) {
              issues.push({
                message: `${String(value)} is not a value of ${type}.${node.attribute} (${shape.values
                  .map((v) => String(v))
                  .join(', ')})`,
                path: [...path, node.kind === 'equals' ? 'value' : 'values'],
              });
            }
          }
        }
        return;
      }

      case 'where': {
        const spec = NAVIGATION_SPECS[node.path];
        const illegal = types.filter((type) => !spec.from.includes(type));
        if (illegal.length > 0) {
          issues.push({
            message: `"${node.path}" cannot be navigated from ${illegal.join(', ')}`,
            path: [...path, 'path'],
          });
          return;
        }
        if (node.of_type !== null && !spec.to.includes(node.of_type)) {
          issues.push({
            message: `"${node.path}" reaches ${spec.to.join(' or ')}, not ${node.of_type}`,
            path: [...path, 'of_type'],
          });
          return;
        }
        const reached = node.of_type === null ? spec.to : [node.of_type];
        walk(node.of, reached, [...path, 'of']);
        return;
      }
    }
  };

  walk(expr, [subjectType], []);
  return issues;
}

/**
 * Every canonical object type this predicate could reach, including the subject.
 *
 * `requires_object_types` is the pre-selection filter (§8.3 step 1) and is
 * authored, not derived — it is the cheap check that runs before evaluation, so
 * computing it from the predicate on every load would defeat its purpose. This
 * is what keeps the authored value honest: a filter naming a type the predicate
 * can never reach excludes models for no reason, and the pattern then silently
 * never fires.
 */
export function reachableObjectTypes(
  expr: PredicateExpr,
  subjectType: CanonicalObjectType,
): Set<CanonicalObjectType> {
  const reached = new Set<CanonicalObjectType>([subjectType]);

  const walk = (node: PredicateExpr, types: readonly PredicateObjectType[]): void => {
    switch (node.kind) {
      case 'all':
      case 'any':
        for (const child of node.of) walk(child, types);
        return;
      case 'not':
        walk(node.of, types);
        return;
      case 'where': {
        const spec = NAVIGATION_SPECS[node.path];
        const next = node.of_type === null ? spec.to : [node.of_type];
        for (const type of next) {
          if (type !== 'system') reached.add(type);
        }
        walk(node.of, next);
        return;
      }
      case 'empty': {
        // A pattern testing `crosses_boundary_ids` needs a trust boundary to
        // exist even though it never navigates to one.
        for (const type of types) {
          const shape = shapeOf(type, node.attribute);
          if (shape?.kind !== 'collection') continue;
          for (const referenced of shape.references) reached.add(referenced);
        }
        return;
      }
      default:
        return;
    }
  };

  walk(expr, [subjectType]);
  return reached;
}

/** One attribute an expression names, and the object type it names it on. */
export interface AttributeRead {
  type: PredicateObjectType;
  attribute: string;
}

/**
 * Every attribute this expression *could* read, statically.
 *
 * Distinct from the witnesses evaluation returns, and the difference matters:
 * this is the whole surface a pattern touches, which is what a review sheet
 * shows and what `BLOCKED.md` counts against the attributes the pipeline never
 * establishes. Witnesses are the subset one particular verdict actually rested
 * on, which is what confidence is computed from (ZD-029 §5). Using this where
 * witnesses belong would reinstate the over-collection ZD-020 exists to remove.
 */
export function predicateReads(
  expr: PredicateExpr,
  subjectType: PredicateObjectType,
): AttributeRead[] {
  const reads = new Map<string, AttributeRead>();
  const record = (type: PredicateObjectType, attribute: string): void => {
    reads.set(`${type}.${attribute}`, { type, attribute });
  };

  const walk = (node: PredicateExpr, types: readonly PredicateObjectType[]): void => {
    switch (node.kind) {
      case 'all':
      case 'any':
        for (const child of node.of) walk(child, types);
        return;
      case 'not':
        walk(node.of, types);
        return;
      case 'equals':
      case 'one_of':
      case 'includes':
      case 'established_as':
      case 'empty':
        for (const type of types) record(type, node.attribute);
        return;
      case 'where': {
        const spec = NAVIGATION_SPECS[node.path];
        for (const type of types) {
          for (const attribute of spec.reads) record(type, attribute);
          if (
            node.path === 'applied_control' &&
            (type === 'data_flow' || type === 'control_flow')
          ) {
            // ZD-030's boundary-mediated route reads the flow's derived
            // crossing, which is not in `spec.reads` because
            // `controlsCovering` decides whether it was consulted at all.
            record(type, 'crosses_boundary_ids');
          }
        }
        walk(node.of, node.of_type === null ? spec.to : [node.of_type]);
        return;
      }
      case 'present':
        return;
    }
  };

  walk(expr, [subjectType]);
  return [...reads.values()].sort((a, b) =>
    `${a.type}.${a.attribute}`.localeCompare(`${b.type}.${b.attribute}`),
  );
}

/**
 * The component types this expression tests for, anywhere in the tree.
 *
 * `requires_object_types` filters on the canonical object *type*, which is too
 * coarse for a pack whose AI patterns all hinge on a component's `type`: every
 * model has components, so an AI pattern survives pre-selection against a ferry
 * booking system and only then evaluates to `false` — or, where some other
 * clause is undecidable, to `unknown`, which surfaces a conditional finding
 * about a vector store the system does not have. That is the noise failure
 * DESIGN_SPEC §2.2 names, produced by a filter that was never fine enough.
 *
 * Used to check `requires_component_types` against the predicate, so the second
 * filter cannot drift into an arbitrary one: a type may be required only if the
 * pattern actually tests for it.
 */
export function componentTypesNamed(
  expr: PredicateExpr,
  subjectType: PredicateObjectType,
): Set<string> {
  const named = new Set<string>();

  const walk = (node: PredicateExpr, types: readonly PredicateObjectType[]): void => {
    switch (node.kind) {
      case 'all':
      case 'any':
        for (const child of node.of) walk(child, types);
        return;
      case 'not':
        walk(node.of, types);
        return;
      case 'where':
        walk(node.of, node.of_type === null ? NAVIGATION_SPECS[node.path].to : [node.of_type]);
        return;
      case 'equals':
        if (node.attribute === 'type' && types.includes('component')) named.add(String(node.value));
        return;
      // `established_as` on `type` names component types exactly as `one_of`
      // does. Included so `requires_component_types` stays checkable against the
      // predicate whichever form an author reaches for — a second copy that
      // knows about only some of the nodes is the drift ZD-039 §6 found in the
      // blocked-list scope check.
      case 'one_of':
      case 'established_as':
        if (node.attribute === 'type' && types.includes('component')) {
          for (const value of node.values) named.add(String(value));
        }
        return;
      default:
        return;
    }
  };

  walk(expr, [subjectType]);
  return named;
}

/**
 * Whether an expression evaluated **on an observed control** constrains its
 * `kinds` on every branch that could be satisfied.
 *
 * `all` needs one conjunct to constrain, because the whole is only true if that
 * one is; `any` needs all of them, because any branch on its own can carry the
 * verdict. `not` and `where` constrain nothing: a negated kind test asks the
 * opposite question, and navigating away from the control leaves it behind.
 */
const constrainsControlKinds = (node: PredicateExpr): boolean => {
  switch (node.kind) {
    case 'includes':
      return node.attribute === 'kinds';
    case 'all':
      return node.of.some(constrainsControlKinds);
    case 'any':
      return node.of.every(constrainsControlKinds);
    default:
      return false;
  }
};

/**
 * Every `applied_control` navigation that reads observed controls kind-blind
 * (docs/DECISIONS.md ZD-039).
 *
 * **This is the mechanical form of the rule ZD-039 exists to install.** Before
 * it, `none(applied_control, present())` meant *"nothing at all protects this"*,
 * so a control of one kind satisfied a pattern about another and the finding
 * moved to a different object — CLAUDE.md principle 9's failure reached through
 * a *true* fact about the client's architecture.
 *
 * **It applies in a boost as well as in a core predicate, and the uniformity is
 * the point.** Only a core predicate can suppress a finding, so a rule scoped
 * to core predicates would be sound today and would stop being sound the moment
 * somebody moved a boost clause into one — a refactor nothing would catch. A
 * rule about where a clause sits is a convention; a rule about the clause is
 * not.
 */
export function unconstrainedControlReads(
  expr: PredicateExpr,
  subjectType: PredicateObjectType,
): PredicateIssue[] {
  const issues: PredicateIssue[] = [];

  const walk = (
    node: PredicateExpr,
    types: readonly PredicateObjectType[],
    path: (string | number)[],
  ): void => {
    switch (node.kind) {
      case 'all':
      case 'any':
        node.of.forEach((child, i) => {
          walk(child, types, [...path, 'of', i]);
        });
        return;
      case 'not':
        walk(node.of, types, [...path, 'of']);
        return;
      case 'where': {
        if (node.path === 'applied_control' && !constrainsControlKinds(node.of)) {
          issues.push({
            message:
              'an applied_control navigation must constrain "kinds" on the controls it reaches: ' +
              'reading ' +
              'observed controls kind-blind lets a control of one kind answer a question about ' +
              'another, which suppresses the finding and moves it to a different object',
            path: [...path, 'of'],
          });
        }
        const spec = NAVIGATION_SPECS[node.path];
        walk(node.of, node.of_type === null ? spec.to : [node.of_type], [...path, 'of']);
        return;
      }
      case 'includes':
        if (node.attribute === 'kinds' && node.values.includes('other')) {
          issues.push({
            message:
              '"other" means the document stated a purpose this vocabulary cannot hold, so a ' +
              'pattern matching on it would be claiming that an unclassifiable control mitigates ' +
              'this specific threat',
            path: [...path, 'values'],
          });
        }
        return;
      default:
        return;
    }
  };

  walk(expr, [subjectType], []);
  return issues;
}

/**
 * The control kinds a pattern's `applied_control` navigations look for
 * (docs/DECISIONS.md ZD-043 §2).
 *
 * **Derived from the predicate, never declared beside it**, for ZD-029 §1's
 * reason: a second list is a second copy that nothing checks against the first,
 * and here the drift lands in a client-facing sentence — a finding saying *"no
 * control of kind retention is observed"* about a pattern that in fact looked
 * for something else. That sentence is the whole precision gain ZD-039 §3 was
 * after, and it is worth nothing if the kinds in it are authored separately
 * from the kinds the predicate used.
 *
 * `unconstrainedControlReads` already refuses an `applied_control` navigation
 * that does not constrain `kinds`, so this is non-empty wherever a pattern
 * reads observed controls at all. Empty means the pattern never reaches one,
 * which is what makes the slot `not_applicable` rather than unestablished.
 */
export function controlKindsSought(expr: PredicateExpr): ControlKind[] {
  const kinds = new Set<string>();

  const collect = (node: PredicateExpr): void => {
    switch (node.kind) {
      case 'includes':
        if (node.attribute === 'kinds') for (const value of node.values) kinds.add(String(value));
        return;
      case 'all':
      case 'any':
        node.of.forEach(collect);
        return;
      case 'not':
        collect(node.of);
        return;
      default:
        return;
    }
  };

  const walk = (node: PredicateExpr): void => {
    switch (node.kind) {
      case 'all':
      case 'any':
        node.of.forEach(walk);
        return;
      case 'not':
        walk(node.of);
        return;
      case 'where':
        if (node.path === 'applied_control') collect(node.of);
        walk(node.of);
        return;
      default:
        return;
    }
  };

  walk(expr);
  /*
   * Filtered rather than cast. `validatePredicate` already refuses an
   * `includes('kinds', …)` naming a value outside the vocabulary, so nothing is
   * dropped here in practice — but a cast would make that a promise this
   * function relies on and cannot see, and the values it walks are
   * `AttributeValue`s rather than kinds.
   */
  return [...kinds]
    .filter((kind): kind is ControlKind => (CONTROL_KINDS as readonly string[]).includes(kind))
    .sort();
}

/**
 * Attributes whose being unestablished stops the evaluation **before** the
 * subtree that depends on them (docs/DECISIONS.md ZD-044 §3).
 *
 * **This is what decides whether a clarification may say answering it settles
 * the question.** `all` and `any` evaluate every child, so where the undecided
 * leaves are ordinary attribute reads, establishing all of them makes the whole
 * expression decide — the claim is honest. A `where` is different: an
 * undetermined navigation returns `unknown` *without evaluating its body*, so
 * the facts behind that navigation were never reached and answering may simply
 * produce the next question. On the corpus that is 102 of 165, and a flat
 * *"answering this decides whether the finding applies"* would over-claim on
 * every one of them.
 *
 * **Union semantics, and the asymmetry is deliberate.** An attribute read both
 * by a navigation and by a leaf in one pattern counts as blocking, because
 * evaluation does not record which read produced the ref and the conservative
 * answer is the weaker claim. Erring the other way would assert that a question
 * settles something when it does not, which is the direction CLAUDE.md
 * principle 5 exists to prevent. No corpus pattern reads an attribute both
 * ways, so the branch is dormant there and is tested against a fixture.
 */
export function navigationBlockingAttributes(expr: PredicateExpr): Set<string> {
  const blocking = new Set<string>();

  const walk = (node: PredicateExpr): void => {
    switch (node.kind) {
      case 'all':
      case 'any':
        node.of.forEach(walk);
        return;
      case 'not':
        walk(node.of);
        return;
      case 'where': {
        /*
         * `applied_control` reads through `controlsCovering`, whose `reads` are
         * empty because it decides for itself what it consulted — so its two
         * undetermined causes are named here rather than taken from the spec,
         * exactly as `predicateReads` names them (ZD-030, ZD-041 §3).
         */
        if (node.path === 'applied_control') {
          blocking.add('applies_to');
          blocking.add('crosses_boundary_ids');
        }
        for (const attribute of NAVIGATION_SPECS[node.path].reads) blocking.add(attribute);
        walk(node.of);
        return;
      }
      default:
        return;
    }
  };

  walk(expr);
  return blocking;
}

/**
 * Attributes this node reads through the conflating *false or unstated* form,
 * however it is spelled (docs/DECISIONS.md ZD-040, ZD-042).
 *
 * `any(eq(a, false), eq(a, 'unknown'))` is `true` on two of a `Tristate`'s
 * three states, so a pattern using it fires identically on a path a document
 * calls unprotected and on one nothing describes. Both disjuncts are
 * individually legitimate — ZD-029 §3 defends `eq(a, 'unknown')` in terms, as a
 * decidable fact *about the documents* — and it is the disjunction that loses
 * the distinction. `established_as` is the form that keeps it.
 *
 * **Two spellings, because a refusal you can evade by rewriting is a
 * convention.** The disjunctive form is matched at any arity rather than only
 * at two, and `one_of(a, [false, 'unknown'])` says the same thing in one node.
 * Nothing here looks for calls to a constructor: a pack is data by the time
 * anything reads it, so an author writing the tree by hand must meet the same
 * refusal.
 *
 * **`false` specifically, and that is not an accident of where this came from.**
 * The defect is asserting a **negative** on the strength of silence, and `false`
 * is what makes the claim a negative — which is why `isAbsenceMarker` excludes
 * it (ZD-020). Pairing a marker with a *positive* value, as
 * `any(eq(type, 'db'), eq(type, 'unknown'))` would, over-fires instead, and
 * over-firing is the direction CLAUDE.md principle 9 says to err in; a
 * conservative sweep written that way is a legitimate thing to want and stays
 * sayable. Widening this to every marker-plus-value pair would refuse it for a
 * symmetry the rule does not have.
 */
function conflatingReads(node: PredicateExpr): string[] {
  const conflates = (values: readonly AttributeValue[]): boolean =>
    values.includes(false) && values.some(isAbsenceMarker);

  if (node.kind === 'one_of') return conflates(node.values) ? [node.attribute] : [];
  if (node.kind !== 'any') return [];
  const byAttribute = new Map<string, AttributeValue[]>();
  for (const child of node.of) {
    if (child.kind !== 'equals') continue;
    byAttribute.set(child.attribute, [...(byAttribute.get(child.attribute) ?? []), child.value]);
  }
  return [...byAttribute].filter(([, values]) => conflates(values)).map(([attribute]) => attribute);
}

/**
 * Every read of a scalar that cannot tell a stated negative from silence
 * (docs/DECISIONS.md ZD-042).
 *
 * **Refused at the pack's own parse rather than declared as a residue**, which
 * is ZD-039's posture one attribute along. That entry deleted the kind-blind
 * `unprotected()` rather than writing a convention against it, on the ground
 * that a dangerous constructor left in the language is one an author will reach
 * for. The same holds here, and `NEVER_ESTABLISHED`'s pinned-at-zero shape is
 * *not* the right analogy: an entry can legitimately return to that list,
 * because a collection nothing establishes is a state of the world. A
 * conflating read is an authoring error, and there is no future in which one is
 * correct — so a declared-residue list for it would be a second copy of this
 * rule with nothing checking the two against each other.
 *
 * Asking whether the documents are silent stays sayable and stays decidable:
 * `eq(a, 'unknown')` alone is untouched, and `it.unknown.*` patterns are built
 * on it.
 */
export function conflatingTristateReads(expr: PredicateExpr): PredicateIssue[] {
  const issues: PredicateIssue[] = [];

  const walk = (node: PredicateExpr, path: (string | number)[]): void => {
    for (const attribute of conflatingReads(node)) {
      issues.push({
        message:
          `"${attribute}" is read through the conflating "false or unstated" form, which is ` +
          'true on a stated negative and on silence alike, so the pattern cannot fire ' +
          'differently on a document that answers the question: use "established_as" to ask ' +
          'what a document states, or "equals" against the absence-marker to ask about silence',
        path,
      });
    }
    switch (node.kind) {
      case 'all':
      case 'any':
        node.of.forEach((child, i) => {
          walk(child, [...path, 'of', i]);
        });
        return;
      case 'not':
        walk(node.of, [...path, 'of']);
        return;
      case 'where':
        walk(node.of, [...path, 'of']);
        return;
      default:
        return;
    }
  };

  // No subject type is needed, and that is deliberate rather than an omission:
  // the rule is about the *form* rather than about which object the attribute
  // sits on, so it holds unchanged for an attribute reached through a
  // navigation and for a scalar this codebase has not added yet.
  walk(expr, []);
  return issues;
}

/** A pattern's subject must be groundable on its own (CLAUDE.md principle 4). */
export function validateSubjectType(subjectType: CanonicalObjectType): PredicateIssue[] {
  return (PRIMARY_GROUNDING_TYPES as readonly CanonicalObjectType[]).includes(subjectType)
    ? []
    : [
        {
          message:
            `a pattern cannot take ${subjectType} as its subject: a finding grounded only on ` +
            `an actor, environment or observed control is not grounded in the architecture`,
          path: ['subject_type'],
        },
      ];
}

// ---------------------------------------------------------------------------
// Evaluation
// ---------------------------------------------------------------------------

/** One attribute a verdict rested on, or failed to resolve (ZD-029 §5). */
export interface AttributeRef {
  object_id: string;
  attribute: string;
}

export type PredicateVerdict = 'true' | 'false' | 'unknown';

export interface PredicateOutcome {
  verdict: PredicateVerdict;
  /** The attributes actually consulted to reach the verdict. */
  witnesses: AttributeRef[];
  /** Objects the satisfied evaluation navigated to — the grounding set, less the subject. */
  objects: string[];
  /** The attributes that could not be resolved, where the verdict is `unknown`. */
  undetermined: AttributeRef[];
}

export interface PredicateContext {
  model: CanonicalModel;
  objects: CanonicalObjectIndex;
}

interface Target {
  id: string;
  type: PredicateObjectType;
  values: Readonly<Record<string, unknown>>;
  provenanced: Provenanced;
  /** The system is readable but never grounds (ZD-029 §7). */
  groundable: boolean;
}

const EMPTY: PredicateOutcome = { verdict: 'true', witnesses: [], objects: [], undetermined: [] };

const key = (ref: AttributeRef): string => `${ref.object_id}|${ref.attribute}`;

const mergeRefs = (lists: readonly (readonly AttributeRef[])[]): AttributeRef[] => {
  const seen = new Map<string, AttributeRef>();
  for (const list of lists) for (const ref of list) seen.set(key(ref), ref);
  return [...seen.values()].sort((a, b) => key(a).localeCompare(key(b)));
};

const mergeIds = (lists: readonly (readonly string[])[]): string[] =>
  [...new Set(lists.flat())].sort();

const combine = (
  verdict: PredicateVerdict,
  parts: readonly PredicateOutcome[],
): PredicateOutcome => ({
  verdict,
  witnesses: mergeRefs(parts.map((p) => p.witnesses)),
  objects: mergeIds(parts.map((p) => p.objects)),
  undetermined: mergeRefs(parts.map((p) => p.undetermined)),
});

const targetOf = (ctx: PredicateContext, id: string): Target | null => {
  const entry = ctx.objects.get(id);
  if (!entry) return null;
  return { id, type: entry.type, values: entry.values, provenanced: entry, groundable: true };
};

const systemTarget = (ctx: PredicateContext): Target => ({
  id: SYSTEM_OBJECT_ID,
  type: 'system',
  values: ctx.model.system,
  provenanced: ctx.model.system,
  groundable: false,
});

/** Whether the model established this attribute separately (ZD-020, ZD-027 rule 2). */
const isEstablished = (target: Target, attribute: string): boolean =>
  establishedAttributes(target.provenanced).includes(attribute);

const CONFIDENCE_RANK: Record<Confidence, number> = { low: 0, medium: 1, high: 2 };

/**
 * How well supported a satisfied branch is — the weakest of its witnesses.
 *
 * A branch with no witnesses ranks highest: it read nothing, so there is
 * nothing about it to doubt. This is what `any` selects on (ZD-029 §5), and it
 * mirrors ZD-026 §5's rule that an agreeing value takes the strongest basis
 * among the sources that stated it.
 */
function branchStrength(ctx: PredicateContext, outcome: PredicateOutcome): number {
  let rank = CONFIDENCE_RANK.high;
  for (const witness of outcome.witnesses) {
    const provenanced: Provenanced | undefined =
      witness.object_id === SYSTEM_OBJECT_ID
        ? ctx.model.system
        : ctx.objects.get(witness.object_id);
    if (!provenanced) continue;
    rank = Math.min(rank, CONFIDENCE_RANK[attributeConfidence(provenanced, witness.attribute)]);
  }
  return rank;
}

/**
 * Navigates one step, reporting what it read and whether it could tell.
 *
 * The `undetermined` case is the whole reason this returns a record: a
 * navigation across an attribute the model never established must not look like
 * a navigation that found nothing (ZD-027 rule 2), or "no asset on this flow is
 * classified `pii`" comes out `true` by reading a list nobody populated.
 */
function navigate(
  ctx: PredicateContext,
  from: Target,
  path: PredicateNavigation,
): {
  targets: Target[];
  reads: AttributeRef[];
  /** What a *negative* answer rested on, where that differs (ZD-030). */
  readsWhenEmpty: AttributeRef[];
  undetermined: AttributeRef[];
} {
  if (path === 'system') {
    return { targets: [systemTarget(ctx)], reads: [], readsWhenEmpty: [], undetermined: [] };
  }

  if (path === 'applied_control') {
    const coverage = controlsCovering(ctx.model, from.id);
    const targets = coverage.applies
      .map((a) => targetOf(ctx, a.control_id))
      .filter((t): t is Target => t !== null);
    return {
      targets,
      reads: coverage.reads.map((r) => ({ object_id: r.object_id, attribute: r.attribute })),
      readsWhenEmpty: coverage.examined.map((r) => ({
        object_id: r.object_id,
        attribute: r.attribute,
      })),
      // Passed straight through: `controlsCovering` knows which of its two
      // causes fired and this does not, so re-deriving a ref here is what
      // produced fourteen wrong questions (ZD-041 §3).
      undetermined: coverage.undetermined,
    };
  }

  const spec = NAVIGATION_SPECS[path];
  const reads: AttributeRef[] = [];
  const undetermined: AttributeRef[] = [];
  const ids: string[] = [];

  for (const attribute of spec.reads) {
    const shape = shapeOf(from.type, attribute);
    // Endpoints are structurally guaranteed to resolve (`checkModelIntegrity`
    // refuses a dangling reference), so a reference navigation is always
    // determined. A collection may simply never have been worked out.
    if (shape?.kind === 'collection' && !isEstablished(from, attribute)) {
      undetermined.push({ object_id: from.id, attribute });
      continue;
    }
    reads.push({ object_id: from.id, attribute });
    const value = from.values[attribute];
    if (Array.isArray(value)) ids.push(...(value as string[]));
    else if (typeof value === 'string') ids.push(value);
  }

  const targets = ids
    .map((id) => targetOf(ctx, id))
    .filter((t): t is Target => t !== null)
    .filter((t) => spec.to.includes(t.type));

  return { targets, reads, readsWhenEmpty: reads, undetermined };
}

function evaluateNode(
  node: PredicateExpr,
  target: Target,
  ctx: PredicateContext,
): PredicateOutcome {
  switch (node.kind) {
    case 'present':
      return EMPTY;

    case 'all': {
      const parts = node.of.map((child) => evaluateNode(child, target, ctx));
      const falses = parts.filter((p) => p.verdict === 'false');
      if (falses.length > 0) return combine('false', falses);
      const unknowns = parts.filter((p) => p.verdict === 'unknown');
      if (unknowns.length > 0) return combine('unknown', unknowns);
      return combine('true', parts);
    }

    case 'any': {
      const parts = node.of.map((child) => evaluateNode(child, target, ctx));
      const trues = parts.filter((p) => p.verdict === 'true');
      if (trues.length > 0) {
        // The strongest-supported satisfied branch, tie-broken by declaration
        // order. Taking the union instead would drag a finding down for an
        // alternative reason it never relied on — ZD-020's over-degradation
        // reintroduced through the back door.
        let best = trues[0]!;
        let bestRank = branchStrength(ctx, best);
        for (const candidate of trues.slice(1)) {
          const rank = branchStrength(ctx, candidate);
          if (rank > bestRank) {
            best = candidate;
            bestRank = rank;
          }
        }
        return { ...best, verdict: 'true' };
      }
      const unknowns = parts.filter((p) => p.verdict === 'unknown');
      if (unknowns.length > 0) return combine('unknown', unknowns);
      return combine('false', parts);
    }

    case 'not': {
      const inner = evaluateNode(node.of, target, ctx);
      const verdict: PredicateVerdict =
        inner.verdict === 'true' ? 'false' : inner.verdict === 'false' ? 'true' : 'unknown';
      return { ...inner, verdict };
    }

    case 'equals':
    case 'one_of': {
      const value = target.values[node.attribute];
      const wanted = node.kind === 'equals' ? [node.value] : node.values;
      const witness: AttributeRef = { object_id: target.id, attribute: node.attribute };
      return {
        verdict: wanted.includes(value as AttributeValue) ? 'true' : 'false',
        witnesses: [witness],
        objects: [],
        undetermined: [],
      };
    }

    case 'empty': {
      const witness: AttributeRef = { object_id: target.id, attribute: node.attribute };
      if (!isEstablished(target, node.attribute)) {
        return { verdict: 'unknown', witnesses: [], objects: [], undetermined: [witness] };
      }
      const value = target.values[node.attribute];
      return {
        verdict: Array.isArray(value) && value.length === 0 ? 'true' : 'false',
        witnesses: [witness],
        objects: [],
        undetermined: [],
      };
    }

    case 'established_as': {
      // The whole of ZD-040 in five lines. An attribute the model never
      // established makes the question undecidable rather than answering it —
      // so a flow whose authentication nobody stated routes to the pattern's
      // `unknown_policy`, while a flow a document states is unauthenticated
      // reaches `true` with that document's own citation behind the witness.
      const witness: AttributeRef = { object_id: target.id, attribute: node.attribute };
      if (!isEstablished(target, node.attribute)) {
        return { verdict: 'unknown', witnesses: [], objects: [], undetermined: [witness] };
      }
      const value = target.values[node.attribute];
      return {
        verdict: node.values.includes(value as AttributeValue) ? 'true' : 'false',
        witnesses: [witness],
        objects: [],
        undetermined: [],
      };
    }

    case 'includes': {
      // Three-valued for the same reason `empty` is (ZD-029 §3, ZD-039): a
      // collection nothing established says nothing, and reading its literal
      // `[]` as "holds none of these" is the denial that turns a question into
      // a claim. Here that claim would be *"no control of this kind protects
      // the object"*, which is the missed-material-threat direction.
      const witness: AttributeRef = { object_id: target.id, attribute: node.attribute };
      if (!isEstablished(target, node.attribute)) {
        return { verdict: 'unknown', witnesses: [], objects: [], undetermined: [witness] };
      }
      const value = target.values[node.attribute];
      const held = new Set(Array.isArray(value) ? (value as unknown[]) : []);
      return {
        verdict: node.values.some((wanted) => held.has(wanted)) ? 'true' : 'false',
        witnesses: [witness],
        objects: [],
        undetermined: [],
      };
    }

    case 'where': {
      const step = navigate(ctx, target, node.path);
      const reached = step.targets.filter((t) => node.of_type === null || t.type === node.of_type);

      if (step.undetermined.length > 0) {
        return {
          verdict: 'unknown',
          witnesses: [],
          objects: [],
          undetermined: mergeRefs([step.undetermined]),
        };
      }

      const parts = reached.map((t) => ({
        outcome: evaluateNode(node.of, t, ctx),
        groundable: t.groundable,
        id: t.id,
      }));

      const cite = (chosen: typeof parts): PredicateOutcome =>
        combine('true', [
          { ...EMPTY, witnesses: step.reads },
          ...chosen.map((p) => ({
            ...p.outcome,
            objects: p.groundable ? mergeIds([p.outcome.objects, [p.id]]) : p.outcome.objects,
          })),
        ]);

      if (node.quantifier === 'some') {
        const satisfied = parts.filter((p) => p.outcome.verdict === 'true');
        if (satisfied.length > 0) return cite(satisfied);
        const unknowns = parts.filter((p) => p.outcome.verdict === 'unknown');
        if (unknowns.length > 0) {
          return combine(
            'unknown',
            unknowns.map((p) => p.outcome),
          );
        }
        // **The bodies' witnesses belong here.** A `some` that reached objects
        // and found every one of them false rested on whatever each body read,
        // not only on the navigation — *"no control covering this store is of
        // kind retention"* rests on each covering control's `kinds`, which is
        // exactly the attribute a client would dispute. Dropping them is the
        // under-collection ZD-029 §5 calls worse in kind, because the finding
        // then reads as more certain than its inputs support. It was latent
        // until ZD-039: every negative in the packs had a `present()` body,
        // which reads nothing.
        return {
          verdict: 'false',
          witnesses: mergeRefs([step.readsWhenEmpty, ...parts.map((p) => p.outcome.witnesses)]),
          objects: [],
          undetermined: [],
        };
      }

      // `every` over an empty set is vacuously true, and that is a trap: "every
      // asset this flow carries is encrypted" must not hold because nothing
      // established what it carries. The navigation's own establishment check
      // above already refuses that case, so an empty set here means the model
      // really does say the collection is empty.
      const failures = parts.filter((p) => p.outcome.verdict === 'false');
      if (failures.length > 0) {
        return combine(
          'false',
          failures.map((p) => p.outcome),
        );
      }
      const unknowns = parts.filter((p) => p.outcome.verdict === 'unknown');
      if (unknowns.length > 0) {
        return combine(
          'unknown',
          unknowns.map((p) => p.outcome),
        );
      }
      return cite(parts);
    }
  }
}

/**
 * Evaluates a predicate against one subject, returning the verdict together
 * with what it rested on.
 *
 * `objects` excludes the subject: the caller adds it, because the subject
 * grounds the finding whether or not the predicate navigated anywhere.
 */
export function evaluatePredicate(
  expr: PredicateExpr,
  ctx: PredicateContext,
  subjectId: string,
): PredicateOutcome {
  const subject = targetOf(ctx, subjectId);
  if (!subject) {
    return {
      verdict: 'false',
      witnesses: [],
      objects: [],
      undetermined: [],
    };
  }
  return evaluateNode(expr, subject, ctx);
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

const listOf = (values: readonly AttributeValue[]): string =>
  values.length === 1
    ? String(values[0])
    : `${values.slice(0, -1).map(String).join(', ')} or ${String(values[values.length - 1])}`;

/**
 * The predicate in English, rendered from the tree.
 *
 * Rendered rather than hand-glossed, deliberately: a written gloss beside a
 * predicate is a second copy that drifts, and the review sheets a human reads
 * (docs/DECISIONS.md ZD-033) are only trustworthy if the sentence and the rule
 * cannot disagree.
 */
export function describePredicate(expr: PredicateExpr): string {
  /**
   * A composite nested inside another is bracketed.
   *
   * Without this, `all(any(a, b), not(c))` reads as "either a, or b, and not c"
   * — a sentence whose precedence a reader cannot recover, in the artefact
   * whose entire purpose is that a reader can check the rule against the words
   * (ZD-033). Brackets are ugly and unambiguous; the ambiguity is worse.
   */
  const composite = (node: PredicateExpr): boolean =>
    (node.kind === 'all' || node.kind === 'any') && node.of.length > 1;

  const render = (node: PredicateExpr): string => {
    switch (node.kind) {
      case 'present':
        return 'it exists';
      case 'all':
        return node.of.length === 1 ? render(node.of[0]!) : node.of.map(nested).join(' and ');
      case 'any':
        return node.of.length === 1 ? render(node.of[0]!) : node.of.map(nested).join(' or ');
      case 'not':
        return `it is not the case that ${nested(node.of)}`;
      case 'equals':
        return `its ${node.attribute} is ${String(node.value)}`;
      case 'one_of':
        return `its ${node.attribute} is ${listOf(node.values)}`;
      case 'includes':
        return `its ${node.attribute} includes ${listOf(node.values)}`;
      case 'established_as':
        return `its ${node.attribute} is established as ${listOf(node.values)}`;
      case 'empty':
        return `its ${node.attribute} is empty`;
      case 'where': {
        const spec = NAVIGATION_SPECS[node.path];
        const scope =
          node.of_type === null ? spec.phrase : `${spec.phrase} of type ${node.of_type}`;
        const quantifier = node.quantifier === 'some' ? 'at least one of' : 'every one of';
        return `${quantifier} ${scope} is such that ${nested(node.of)}`;
      }
    }
  };

  const nested = (node: PredicateExpr): string =>
    composite(node) ? `(${render(node)})` : render(node);

  return render(expr);
}
